import { type FocusEvent, useCallback, useEffect, useState } from "react";

import { useLocation } from "react-router-dom";

import { BREAKPOINTS_MAX } from "@dexibit/styles";

const canHover = () => window.matchMedia("(hover: hover)").matches;

export const useNavigation = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [expandedAccordions, setExpandedAccordions] = useState<Set<string>>(new Set());
  const location = useLocation();

  const onMouseEnter = useCallback(() => {
    if (canHover()) setIsHovered(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    if (canHover()) setIsHovered(false);
  }, []);

  // No canHover() guard — keyboard users need this regardless of pointer type
  const onSidebarFocus = useCallback(() => {
    setIsFocusWithin(true);
  }, []);

  const onSidebarBlur = useCallback((e: FocusEvent<HTMLElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsFocusWithin(false);
    }
  }, []);

  const toggleMobileDrawer = useCallback(() => {
    setIsMobileDrawerOpen((prev) => !prev);
  }, []);

  const closeMobileDrawer = useCallback(() => {
    setIsMobileDrawerOpen(false);
  }, []);

  const toggleAccordion = useCallback((key: string) => {
    setExpandedAccordions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Close mobile drawer on resize past tablet breakpoint
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${BREAKPOINTS_MAX.mobile}px)`);
    const handler = (e: MediaQueryListEvent) => {
      if (!e.matches) setIsMobileDrawerOpen(false);
    };
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  // Close mobile drawer on route change
  useEffect(() => {
    setIsMobileDrawerOpen(false);
  }, [location.pathname]);

  // Close mobile drawer on Escape key
  useEffect(() => {
    if (!isMobileDrawerOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsMobileDrawerOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isMobileDrawerOpen]);

  const openAccordion = useCallback((key: string) => {
    setExpandedAccordions((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  return {
    isHovered,
    isFocusWithin,
    isMobileDrawerOpen,
    expandedAccordions,
    onMouseEnter,
    onMouseLeave,
    onSidebarFocus,
    onSidebarBlur,
    toggleMobileDrawer,
    closeMobileDrawer,
    toggleAccordion,
    openAccordion,
  };
};
